import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';

const LOGO_URL = '/logo-givinghopes.png';

export default function Footer() {
  const { apiClient } = useAuth();
  const [email, setEmail] = useState('');
  const [subscriberType, setSubscriberType] = useState('parent');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await apiClient.post('/api/email-subscribe', { email, subscriber_type: subscriberType });
      toast.success('Subscribed successfully!');
      setEmail('');
    } catch {
      toast.error('Failed to subscribe');
    }
  };

  return (
    <footer className="bg-[var(--text-headline)] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Giving Hope" className="h-10 w-10 object-contain" />
              <span className="text-lg font-heading font-bold">Giving Hope LLC</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Maryland's leading psychiatric and mental health service provider, empowering individuals through personalized, compassionate care.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-heading font-bold mb-4 text-[var(--primary-green)]">Quick Links</h4>
            <div className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/training', label: 'Training Portal' },
                { to: '/shop', label: 'Digital Shop' },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="block text-sm text-slate-400 hover:text-white transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-heading font-bold mb-4 text-[var(--primary-green)]">Contact</h4>
            <div className="space-y-3 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-[var(--primary-blue)]" />
                <a href="mailto:admin@givinghopes.com" className="hover:text-white transition-colors">admin@givinghopes.com</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-[var(--primary-blue)]" />
                <a href="tel:4105058875" className="hover:text-white transition-colors">(410) 505-8875</a>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-[var(--primary-blue)] mt-0.5" />
                <span>1301 York Rd Ste 800 #1205<br />Lutherville, MD 21093</span>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-heading font-bold mb-4 text-[var(--primary-green)]">Stay Connected</h4>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)]"
                data-testid="footer-email-input"
              />
              <div className="flex gap-4 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" value="parent" checked={subscriberType === 'parent'} onChange={(e) => setSubscriberType(e.target.value)} className="w-3 h-3" />
                  <span className="text-slate-400">Parent</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" value="clinician" checked={subscriberType === 'clinician'} onChange={(e) => setSubscriberType(e.target.value)} className="w-3 h-3" />
                  <span className="text-slate-400">Clinician</span>
                </label>
              </div>
              <button type="submit" className="w-full bg-[var(--primary-blue)] text-white text-sm py-2.5 rounded-xl font-semibold hover:bg-blue-700 transition-colors" data-testid="footer-subscribe-btn">
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <p>&copy; {new Date().getFullYear()} Giving Hope LLC. All rights reserved.</p>
          <p>Developed by <a href="https://projexino.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors font-semibold">Projexino Solutions Pvt Ltd</a></p>
        </div>
      </div>
    </footer>
  );
}
